"use strict";
// Copyright (c) 2026 The Bitcoin developers
// Distributed under the MIT software license, see the accompanying
// file COPYING or http://www.opensource.org/licenses/mit-license.php.
Object.defineProperty(exports, "__esModule", { value: true });
exports.decodeInputData = decodeInputData;
const bytes_js_1 = require("./io/bytes.js");
const op_js_1 = require("./op.js");
const hash_js_1 = require("./hash.js");
const address_js_1 = require("./address/address.js");
const opcode_js_1 = require("./opcode.js");
/**
 * Decode scriptSig from a P2SH input that uses the Agora-style input-data format.
 *
 * Built: scriptSig = [lokad, data, sig, pubkey, redeemScript]
 * Redeem script: OP_CHECKSIG pushBytes(data) OP_EQUALVERIFY pushBytes(lokad) OP_EQUAL
 *
 * @param scriptSig - The scriptSig bytes from the spending input
 * @param prefix - Address prefix (default 'ecash')
 * @returns Decoded lokadId, data, and address, or undefined if the format doesn't match
 */
function decodeInputData(scriptSig, prefix = 'ecash') {
    const pushes = [];
    const bytes = new bytes_js_1.Bytes(scriptSig);
    while (bytes.data.length - bytes.idx > 0) {
        try {
            const op = (0, op_js_1.readOp)(bytes);
            if ((0, op_js_1.isPushOp)(op)) {
                pushes.push(op.data);
            }
        }
        catch {
            return undefined;
        }
    }
    // scriptSig = <lokad> <data> <sig> <pubkey> <redeemScript>
    if (pushes.length < 5) {
        return undefined;
    }
    const lokadId = pushes[0];
    if (lokadId.length !== 4) {
        return undefined;
    }
    const data = pushes[1];
    const pubkey = pushes[pushes.length - 2];
    const redeemScript = pushes[pushes.length - 1];
    if (pubkey.length !== 33 && pubkey.length !== 65) {
        return undefined;
    }
    // Redeem script: OP_CHECKSIGVERIFY pushBytes(data) OP_EQUALVERIFY pushBytes(lokad) OP_EQUAL
    const redeemBytes = new bytes_js_1.Bytes(redeemScript);
    try {
        if ((0, op_js_1.readOp)(redeemBytes) !== opcode_js_1.OP_CHECKSIGVERIFY) {
            return undefined;
        }
        const dataOp = (0, op_js_1.readOp)(redeemBytes);
        if (!(0, op_js_1.isPushOp)(dataOp)) {
            return undefined;
        }
        if (dataOp.data.length !== data.length ||
            dataOp.data.some((b, i) => b !== data[i])) {
            return undefined;
        }
        if ((0, op_js_1.readOp)(redeemBytes) !== opcode_js_1.OP_EQUALVERIFY) {
            return undefined;
        }
        const lokadOp = (0, op_js_1.readOp)(redeemBytes);
        if (!(0, op_js_1.isPushOp)(lokadOp) || lokadOp.data.length !== 4) {
            return undefined;
        }
        if (lokadOp.data.some((b, i) => b !== lokadId[i])) {
            return undefined;
        }
        if ((0, op_js_1.readOp)(redeemBytes) !== opcode_js_1.OP_EQUAL) {
            return undefined;
        }
    }
    catch {
        return undefined;
    }
    const pkh = (0, hash_js_1.shaRmd160)(pubkey);
    const address = address_js_1.Address.p2pkh(pkh, prefix).toString();
    return { lokadId, data, address };
}
//# sourceMappingURL=inputData.js.map